import { FormGroup, Label, Modal, SubmitButton, Switch } from "@xola/ui-kit";
import { FormField, InputField, requiredString } from "common";
import React from "react";
import { useMutation } from "react-query";
import { SellerPaymentMethod } from "types";
import { boolean, object, string } from "yup";
import { api } from "../../../api/api";
import { useSeller } from "../../../hooks/useAccount";
import { useInvalidate } from "../../../hooks/useInvalidation";
import { SecondaryButton } from "../../Common/SecondaryButton";
import { XolaFormik } from "../../Common/XolaFormik";

const validationSchema = object().shape({
    name: requiredString(),
    comment: object().shape({
        enabled: boolean(),
        required: boolean(),
        title: string().when("enabled", {
            is: true,
            then: requiredString(),
        }),
    }),
});

const initialValues = {
    id: "",
    name: "",
    comment: { enabled: true, required: false, title: "" },
    favourite: false,
};

interface AddPaymentMethodModalProps {
    isOpen?: boolean;
    paymentMethod?: SellerPaymentMethod;
    onClose: () => void;
}

export const EditPaymentMethodModal = ({
    isOpen = false,
    paymentMethod = initialValues,
    onClose,
}: AddPaymentMethodModalProps) => {
    return (
        <Modal isOpen={isOpen} size="medium" onClose={onClose}>
            <ModalContent paymentMethod={paymentMethod} onClose={onClose} />
        </Modal>
    );
};

interface ModalContentProps {
    paymentMethod: SellerPaymentMethod;
    onClose: () => void;
}

const ModalContent = ({ paymentMethod, onClose }: ModalContentProps) => {
    const seller = useSeller();
    const invalidate = useInvalidate();

    const { mutateAsync: savePaymentMethod, isLoading } = useMutation(async (values: SellerPaymentMethod) => {
        if (values.id) {
            return api.put(`/users/${seller.id}/paymentMethods/${values.id}`, values);
        }

        return api.post(`/users/${seller.id}/paymentMethods`, values);
    });

    const handleSubmit = async (values: SellerPaymentMethod) => {
        await savePaymentMethod(values);
        onClose();

        invalidate("sellers", seller.id);
    };

    const isEdit = !!paymentMethod.id;

    return (
        <XolaFormik
            enableReinitialize
            validateOnChange={false}
            validateOnBlur={false}
            validationSchema={validationSchema}
            initialValues={{ ...initialValues, ...paymentMethod }}
            onSubmit={handleSubmit}
        >
            {({ handleSubmit, values, setFieldValue }) => (
                <>
                    <Modal.Header>{isEdit ? "Edit" : "Add"} Payment Method</Modal.Header>

                    <Modal.Body>
                        <FormField name="name" label="Name">
                            <InputField isRequired placeholder="Name" />
                        </FormField>

                        {values.comment.enabled ? (
                            <FormField name="comment.title" label="Comment Label">
                                <InputField isRequired placeholder="Add a Tag" />
                            </FormField>
                        ) : null}

                        <FormGroup>
                            <Label>Comment</Label>
                            <div className="space-y-2">
                                <div>
                                    <Switch.Group>
                                        <Switch
                                            data-testid="edit-payment-method-modal-allowed-comment"
                                            isChecked={values.comment.enabled}
                                            size="medium"
                                            onChange={(value: boolean) => {
                                                setFieldValue("comment.enabled", value);
                                            }}
                                        />
                                        <Switch.Label direction="right" className="ml-3">
                                            Allowed
                                        </Switch.Label>
                                    </Switch.Group>
                                </div>

                                {values.comment.enabled ? (
                                    <div>
                                        <Switch.Group>
                                            <Switch
                                                data-testid="edit-payment-method-modal-required-comment"
                                                isChecked={values.comment.required}
                                                size="medium"
                                                onChange={(value: boolean) => {
                                                    setFieldValue("comment.required", value);
                                                }}
                                            />

                                            <Switch.Label direction="right" className="ml-3">
                                                Required
                                            </Switch.Label>
                                        </Switch.Group>
                                    </div>
                                ) : null}
                            </div>
                        </FormGroup>
                    </Modal.Body>

                    <Modal.Footer className="space-x-4">
                        <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
                        <SubmitButton isLoading={isLoading} color="primary" onClick={handleSubmit}>
                            Save
                        </SubmitButton>
                    </Modal.Footer>
                </>
            )}
        </XolaFormik>
    );
};
